import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';
import { signOut } from 'next-auth/react';
import { useSession } from '@ghq-abi/auth-client-lib/client';
import { useTranslate } from '@tolgee/react';
import nprogress from 'nprogress';

import { useGetProfilePicture } from '~/entities/Employee/hooks/useGetProfilePicture';
import { EntitiesEnum } from '~/shared/auth/permissions';
import { Icon } from '~/shared/components';
import { SignOut } from '~/shared/components/icons/SignOut';
import { useAbility, useSessionInfo } from '~/shared/contexts';
import { User } from '~/shared/hooks/useUser';
import { isProxyEnabled } from '~/shared/utils/environments';

type UseUserDropdownProps = {
  user: User;
};

export const useUserDropdown = ({ user }: UseUserDropdownProps) => {
  const { t } = useTranslate();
  const { proxyOut } = useSession();
  const { sessionInfo } = useSessionInfo();
  const router = useRouter();
  const ability = useAbility();

  const isProxying = !!sessionInfo?.proxiedAs;

  const handleSignOut = useCallback(async () => {
    await signOut({ redirect: false });
  }, []);

  const goToProxy = useCallback(async () => {
    const isEibRoute = router.pathname.startsWith('/eib');
    const proxyRoute = isEibRoute ? '/eib/proxy' : '/myaccess/proxy';
    await router.push(proxyRoute);
  }, [router]);

  const handleStopProxy = useCallback(async () => {
    nprogress.start();

    await proxyOut();
    router.reload();
  }, [proxyOut, router]);

  const items = useMemo(() => {
    const dropdownItems = [
      {
        label: t('common.sign_out', 'Sign Out'),
        onSelect: handleSignOut,
        icon: <SignOut />
      }
    ];

    if (ability.can('proxy', EntitiesEnum.User) && isProxyEnabled()) {
      dropdownItems.unshift({
        label: t(
          isProxying ? 'common.stop_proxy' : 'common.proxy_as_user',
          isProxying ? 'Stop Proxy' : 'Proxy as User'
        ),
        onSelect: isProxying ? handleStopProxy : goToProxy,
        icon: isProxying ? (
          <Icon.StopProxy size={18} />
        ) : (
          <Icon.ProxyIcon size={18} />
        )
      });
    }

    return dropdownItems;
  }, [ability, goToProxy, handleSignOut, handleStopProxy, isProxying, t]);

  const { data } = useGetProfilePicture(user.globalId ?? '');
  const imageSrc = data ? URL.createObjectURL(data) : undefined;

  const profileInfo = {
    name: user.name ?? '',
    urlImage: imageSrc,
    zone: user.zone ?? ''
  };

  return {
    items,
    profileInfo
  };
};
